import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, Grid, Paper, Typography, Avatar, Button, Chip, Rating,
  Divider, CircularProgress, Tab, Tabs, Card, CardContent, LinearProgress,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField
} from '@mui/material';
import {
  Star as StarIcon, Language as LanguageIcon, School as SchoolIcon,
  Verified as VerifiedIcon, CalendarToday, AccessTime, Person,
  ArrowBack, Send as SendIcon
} from '@mui/icons-material';
import { getTeacherPublicProfile } from '../../api/marketplace';
import { getRatingBreakdown } from '../../api/reviews';
import { getOrCreateConversation, sendMessage } from '../../api/messages';
import { getCurrentLanguage } from '../../utils/navigation';
import { useAuth } from '../../store/authStore';

const TeacherProfile = () => {
  const { teacherId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const lang = getCurrentLanguage();

  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [ratingBreakdown, setRatingBreakdown] = useState({});
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgText, setMsgText] = useState('');
  const [msgSending, setMsgSending] = useState(false);
  const [msgSuccess, setMsgSuccess] = useState('');

  const handleSendMessage = async () => {
    if (!msgText.trim() || !user?.id) return;
    setMsgSending(true);
    try {
      const conv = await getOrCreateConversation(
        user.id, teacherId,
        user.displayName || 'Student',
        teacher.displayName || 'Teacher'
      );
      await sendMessage(conv.id, user.id, user.displayName || 'Student', msgText.trim());
      setMsgSuccess('Message sent!');
      setMsgText('');
      setTimeout(() => { setMsgOpen(false); setMsgSuccess(''); }, 1500);
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setMsgSending(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, [teacherId]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const profile = await getTeacherPublicProfile(teacherId);
      setTeacher(profile);
      const breakdown = await getRatingBreakdown(teacherId);
      setRatingBreakdown(breakdown);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getCurrencySymbol = (currency) => {
    switch (currency) {
      case 'EUR': return '€';
      case 'MAD': return 'د.م.';
      case 'GBP': return '£';
      default: return '$';
    }
  };

  const getDayName = (day) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[day];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !teacher) {
    return (
      <Box sx={{ pt: 12, textAlign: 'center' }}>
        <Alert severity="error" sx={{ maxWidth: 500, mx: 'auto' }}>
          {error || 'Teacher not found'}
        </Alert>
      </Box>
    );
  }

  const totalRatings = Object.values(ratingBreakdown).reduce((a, b) => a + b, 0);

  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', pt: 10, pb: 4 }}>
      <Container maxWidth="lg">
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back to search
        </Button>

        <Grid container spacing={3}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            {/* Profile Header */}
            <Paper sx={{ p: 4, mb: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={3}>
                  <Avatar
                    src={teacher.photoURL}
                    sx={{ width: 140, height: 140, mx: 'auto', fontSize: 56 }}
                  >
                    {teacher.displayName?.charAt(0)}
                  </Avatar>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h4" fontWeight={700}>
                      {teacher.displayName}
                    </Typography>
                    {teacher.profileApproved && <VerifiedIcon color="primary" />}
                  </Box>

                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    {teacher.country}
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {teacher.languages?.map(lang => (
                      <Chip
                        key={lang} label={lang} size="small" variant="outlined"
                        icon={<LanguageIcon />}
                        color={teacher.nativeLanguages?.includes(lang) ? 'success' : 'default'}
                      />
                    ))}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                    {teacher.averageRating > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <StarIcon sx={{ color: '#FFB400' }} />
                        <Typography variant="h6" fontWeight={700}>{teacher.averageRating}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          ({teacher.totalReviews} reviews)
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Person fontSize="small" />
                      <Typography variant="body2">{teacher.totalStudents} students</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <SchoolIcon fontSize="small" />
                      <Typography variant="body2">{teacher.totalLessons} lessons</Typography>
                    </Box>
                  </Box>

                  {teacher.specialties?.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {teacher.specialties.map(s => (
                        <Chip key={s} label={s} size="small" color="primary" variant="outlined" />
                      ))}
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Paper>

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
              <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
                <Tab label="About" />
                <Tab label="Reviews" />
                <Tab label="Schedule" />
              </Tabs>
              <Divider />

              <Box sx={{ p: 3 }}>
                {/* About Tab */}
                {tabValue === 0 && (
                  <Box>
                    {teacher.bio && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>About me</Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                          {teacher.bio}
                        </Typography>
                      </Box>
                    )}

                    {teacher.description && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>Description</Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                          {teacher.description}
                        </Typography>
                      </Box>
                    )}

                    {teacher.experience && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>Experience</Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                          {teacher.experience}
                        </Typography>
                      </Box>
                    )}

                    {teacher.education && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>Education</Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                          {teacher.education}
                        </Typography>
                      </Box>
                    )}

                    {teacher.certifications?.length > 0 && (
                      <Box>
                        <Typography variant="h6" fontWeight={600} gutterBottom>Certifications</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {teacher.certifications.map((cert, i) => (
                            <Chip key={i} label={cert} icon={<VerifiedIcon />} variant="outlined" />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Reviews Tab */}
                {tabValue === 1 && (
                  <Box>
                    {/* Rating Summary */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                      <Grid item xs={12} sm={4} textAlign="center">
                        <Typography variant="h2" fontWeight={700}>{teacher.averageRating || '—'}</Typography>
                        <Rating value={teacher.averageRating || 0} readOnly precision={0.5} />
                        <Typography variant="body2" color="text.secondary">
                          {teacher.totalReviews || 0} reviews
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        {[5, 4, 3, 2, 1].map(star => (
                          <Box key={star} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="body2" sx={{ minWidth: 20 }}>{star}</Typography>
                            <StarIcon sx={{ color: '#FFB400', fontSize: 18 }} />
                            <LinearProgress
                              variant="determinate"
                              value={totalRatings ? (ratingBreakdown[star] / totalRatings) * 100 : 0}
                              sx={{ flex: 1, height: 8, borderRadius: 4 }}
                            />
                            <Typography variant="body2" sx={{ minWidth: 30 }}>
                              {ratingBreakdown[star] || 0}
                            </Typography>
                          </Box>
                        ))}
                      </Grid>
                    </Grid>

                    <Divider sx={{ mb: 3 }} />

                    {/* Review List */}
                    {teacher.reviews?.length > 0 ? (
                      teacher.reviews.map(review => (
                        <Box key={review.id} sx={{ mb: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Avatar src={review.studentPhoto} sx={{ width: 40, height: 40 }}>
                              {review.studentName?.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {review.studentName}
                              </Typography>
                              <Rating value={review.rating} readOnly size="small" />
                            </Box>
                          </Box>
                          <Typography variant="body2">{review.comment}</Typography>
                          <Divider sx={{ mt: 2 }} />
                        </Box>
                      ))
                    ) : (
                      <Typography color="text.secondary" textAlign="center">
                        No reviews yet
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Schedule Tab */}
                {tabValue === 2 && (
                  <Box>
                    <Typography variant="h6" fontWeight={600} gutterBottom>Weekly Availability</Typography>
                    {teacher.availability?.length > 0 ? (
                      <Grid container spacing={2}>
                        {[0, 1, 2, 3, 4, 5, 6].map(day => {
                          const daySlots = teacher.availability.filter(a => a.dayOfWeek === day && a.active);
                          if (daySlots.length === 0) return null;
                          return (
                            <Grid item xs={12} sm={6} key={day}>
                              <Card variant="outlined">
                                <CardContent>
                                  <Typography variant="subtitle2" fontWeight={600}>
                                    {getDayName(day)}
                                  </Typography>
                                  {daySlots.map((slot, i) => (
                                    <Chip
                                      key={i}
                                      label={`${slot.startTime} - ${slot.endTime}`}
                                      size="small" sx={{ mr: 0.5, mt: 0.5 }}
                                      icon={<AccessTime />}
                                    />
                                  ))}
                                </CardContent>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    ) : (
                      <Typography color="text.secondary">
                        Contact the teacher to discuss scheduling
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Sidebar - Booking Card */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, position: 'sticky', top: 80 }}>
              <Box textAlign="center" mb={2}>
                <Typography variant="h4" fontWeight={700} color="primary.main">
                  {getCurrencySymbol(teacher.currency)}{teacher.pricePerHour}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  per {teacher.lessonDuration || 50}-min lesson
                </Typography>
              </Box>

              {teacher.trialPrice !== undefined && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Trial lesson: {teacher.trialPrice === 0 ? 'Free' : `${getCurrencySymbol(teacher.currency)}${teacher.trialPrice}`}
                </Alert>
              )}

              <Button
                variant="contained" fullWidth size="large"
                sx={{
                  mb: 1.5, bgcolor: '#F5A623', color: '#fff', py: 1.5,
                  '&:hover': { bgcolor: '#e09500' }, fontWeight: 600
                }}
                onClick={() => navigate(`/${lang}/student/pay/${teacher.id}`)}
              >
                Book trial lesson
              </Button>

              <Button
                variant="outlined" fullWidth size="large"
                onClick={() => setMsgOpen(true)}
              >
                Send message
              </Button>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Response time</Typography>
                  <Typography variant="body2" fontWeight={600}>Within a few hours</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Students</Typography>
                  <Typography variant="body2" fontWeight={600}>{teacher.totalStudents}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Lessons</Typography>
                  <Typography variant="body2" fontWeight={600}>{teacher.totalLessons}</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Send Message Dialog */}
      <Dialog open={msgOpen} onClose={() => setMsgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Message {teacher?.displayName}
        </DialogTitle>
        <DialogContent>
          {msgSuccess ? (
            <Alert severity="success">{msgSuccess}</Alert>
          ) : (
            <TextField
              fullWidth multiline rows={4}
              placeholder="Write your message..."
              value={msgText}
              onChange={(e) => setMsgText(e.target.value)}
              sx={{ mt: 1 }}
              autoFocus
            />
          )}
        </DialogContent>
        {!msgSuccess && (
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setMsgOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={msgSending || !msgText.trim()}
              startIcon={msgSending ? <CircularProgress size={18} /> : <SendIcon />}
              sx={{ bgcolor: '#F5A623', '&:hover': { bgcolor: '#e09500' } }}
            >
              {msgSending ? 'Sending...' : 'Send'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

export default TeacherProfile;
